import {
  call, select, put,
} from 'redux-saga/effects';
import actionTypes from 'store/postflop/actionTypes';
import api from 'store/api';
import { toast } from 'react-toastify';
import { URL } from '../../../constants/url';
import {
  isSpecificSuitHand,
  normalizePostflopSimilarHand,
  shouldRequestPostflopSimilar,
  splitPostflopHand,
} from '../../../utils/postflopHand';
import {
  resolvePostflopDataLocation,
} from '../../../utils/postflopPosition';
import {
  getTableSize, getStackSize, getPositionType, getPosition,
  getResultsPositionType,
  getResultsPosition,
  getSelectedBoard, getPageMode,
} from '../selectors';
import { setSimilarResponse } from '../actions';
import { getRequestRevision, isCurrentRequest, requestMeta } from '../requestState';
import watchRequest from './requestLifecycle';

export function* setPostflopSimilarGen({ payload = {} }) {
  const revision = yield select(getRequestRevision('similar'));
  const meta = requestMeta('similar', revision);
  const game = yield select(getTableSize);
  const bb = yield select(getStackSize);
  const positionType = yield select(getPositionType);
  const position = yield select(getPosition);
  const resultsPositionType = yield select(getResultsPositionType);
  const resultsPosition = yield select(getResultsPosition);
  const selectedBoard = yield select(getSelectedBoard);
  const pageMode = yield select(getPageMode);
  const isResultsMode = pageMode?.value === 'Results';
  const isPlo5Request = typeof payload.isPlo5 === 'boolean'
    ? payload.isPlo5
    : sessionStorage.getItem('isPlo5') === 'true';

  const defineEndpoint = () => {
    if (isPlo5Request) return URL.PLO5_POSTFLOP_SIMILAR;
    return URL.POSTFLOP_SIMILAR;
  };

  try {
    const isStudyMode = pageMode?.value === 'Study';
    const normalizedHand = payload.cards || '';
    if (!shouldRequestPostflopSimilar(normalizedHand, isPlo5Request)) {
      yield put({ type: `${actionTypes.SET_POSTFLOP_SIMILAR}_SUCCESS`, meta });
      yield put({ ...setSimilarResponse.success({ data: null }), meta });
      return;
    }
    const requestHand = normalizePostflopSimilarHand(
      normalizedHand,
      isPlo5Request,
      isStudyMode,
    );
    const specificSuits = !isPlo5Request
      && isStudyMode
      && isSpecificSuitHand(requestHand);
    const requestPositionType = isResultsMode ? resultsPositionType?.value : positionType;
    const requestPosition = isResultsMode ? resultsPosition?.value : position[0];
    const dataLocation = resolvePostflopDataLocation({
      game,
      isPlo5: isPlo5Request,
      bb,
      positionType: requestPositionType,
      spot: requestPosition,
    });

    const res = yield call(api, {
      method: 'post',
      url: defineEndpoint(),
      data: {
        bb,
        game: dataLocation.game,
        position_type: dataLocation.positionType,
        position: dataLocation.position,
        board: selectedBoard,
        previous_actions: (payload.currentStep || ['starting']).join('-'),
        specific_suits: specificSuits,
        hand: requestHand,
      },
    });

    if (!(yield select(isCurrentRequest('similar', revision)))) return;
    const data = res?.data;
    if (!data || typeof data !== 'object') throw new Error('No matching hands are available.');

    if (!isStudyMode && Array.isArray(data.similar) && normalizedHand) {
      data.similar = data.similar.map((item) => ({
        ...item,
        highlight: item.highlight || item.combo === normalizedHand,
      }));
    }

    if (Array.isArray(data.similar) && data.similar.length > 1000) {
      const highlighted = data.similar.find((item) => item.highlight);
      data.similar = data.similar.slice(0, 1000);
      if (highlighted && !data.similar.find((item) => item.combo === highlighted.combo)) {
        data.similar = [highlighted, ...data.similar.slice(0, 999)];
      }
    }

    yield put({ type: 'SET_POSTFLOP_SIMILAR_SUCCESS', payload: data, meta });
    if (payload.fromSearch) {
      yield put({
        ...setSimilarResponse.success({
          data,
          currentCards: splitPostflopHand(normalizedHand),
        }),
        meta,
      });
    } else {
      yield put({ ...setSimilarResponse.success({ data }), meta });
    }
  } catch (e) {
    if (!(yield select(isCurrentRequest('similar', revision)))) return;
    yield put({ ...setSimilarResponse.success({ data: null }), meta });
    yield put({ type: 'SET_POSTFLOP_SIMILAR_ERROR', error: e.message, meta });
    if (e.message !== 'empty hand') {
      yield call(toast.error, e.message);
    }
  }
}

export default function* listener() {
  yield* watchRequest('similar', setPostflopSimilarGen);
}
